import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getLostItems, getFoundItems } from '../services/itemService';
import { LostItem, FoundItem, ItemCategory, ItemStatus } from '../types';
import { CATEGORIES, inputClass, labelClass, capitalize } from '../constants';
import ItemCard from '../components/ItemCard';

type ItemType = 'lost' | 'found';

const CONFIG = {
  lost: {
    title: 'Lost Items',
    description: 'Browse items that have been reported as lost',
    emptyMessage: 'No lost items found',
    reportLink: '/report/lost',
    reportText: 'Report a lost item'
  },
  found: {
    title: 'Found Items',
    description: 'Browse items that have been found and reported',
    emptyMessage: 'No found items available',
    reportLink: '/report/found',
    reportText: 'Report a found item'
  }
};

const ItemsPage: React.FC = () => {
  const { type } = useParams<{ type: ItemType }>();
  const itemType: ItemType = type === 'found' ? 'found' : 'lost';

  const [items, setItems] = useState<(LostItem | FoundItem)[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<ItemCategory | ''>('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const status: ItemStatus = 'active';
      const filters = selectedCategory ? { category: selectedCategory, status } : { status };
      const fetchedItems = itemType === 'lost'
        ? await getLostItems(filters)
        : await getFoundItems(filters);
      setItems(fetchedItems);
    } catch (error) {
      console.error(`Error fetching ${itemType} items:`, error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, itemType]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filteredItems = items.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.location?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const { title, description, emptyMessage, reportLink, reportText } = CONFIG[itemType];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
          <p className="text-gray-600">{description}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="search" className={labelClass}>Search</label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputClass}
                placeholder="Search by title, description, or location..."
              />
            </div>
            <div>
              <label htmlFor="category" className={labelClass}>Category</label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as ItemCategory | '')}
                className={inputClass}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{capitalize(cat)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <ItemCard key={item.id} item={item} type={itemType} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500 text-lg">{emptyMessage}</p>
            <Link to={reportLink} className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium">
              {reportText} →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemsPage;